import { validatePageFilters } from '~/pages/AskRichardUnified/page/pageFilters';

import Core from './core';
import type { FlashCampaign, FlashFilters } from './types';

export function flashFilterSchema(campaigns: FlashCampaign[], current: Partial<FlashFilters> = {}) {
  const enumeration = (values: string[]) => ({ type: 'enum' as const, values: [...new Set(['', ...values])] });
  return {
    tab: enumeration(['overview', 'campaigns', 'funnel', 'comparison']),
    view: enumeration(['portfolio', 'campaign', 'campaigns']),
    rollup: enumeration(['campaign', 'territory', 'pcm']),
    reviewBy: enumeration(['pcm', 'territory']),
    reviewGroup: enumeration(
      current.reviewBy === 'pcm'
        ? campaigns.map((c) => c.pcmName || 'No current-quarter PCM record')
        : current.reviewBy === 'territory'
          ? campaigns.map((c) => c.strategicTerritory || 'Unknown strategic territory')
          : [],
    ),
    sort: enumeration(['gap', 'margin', 'name', 'age']),
    comparability: enumeration(['all', 'comparable', 'uncompared', 'uncovered']),
    trajectoryMeasure: enumeration(['packages', 'margin']),
    historyView: enumeration(['previous', 'weekly']),
    weeklyExclusion: {
      ...enumeration(campaigns.map((c) => c.id)),
      multiple: true,
      maxSelections: Core.MAX_WEEKLY_EXCLUSIONS,
    },
    campaignComparison: enumeration(['previous', 'peers']),
    pcm: enumeration(campaigns.map((c) => c.pcmName || 'No current-quarter PCM record')),
    territory: enumeration(campaigns.map((c) => c.strategicTerritory || 'Unknown strategic territory')),
    market: enumeration(campaigns.flatMap((c) => Object.keys(c.dailyByMarket || {}))),
    campaign: enumeration(campaigns.map((c) => c.id)),
    flashCampaign: enumeration(campaigns.map((c) => c.id)),
    cohort: enumeration(['primary', 'secondary']),
    flashCohort: enumeration(['primary', 'secondary']),
    status: enumeration(['live', 'completed', 'all']),
    country: enumeration(campaigns.map((c) => c.country)),
    destination: enumeration(campaigns.map((c) => c.destination)),
    owner: enumeration(campaigns.map((c) => c.owner)),
    campaignType: enumeration(campaigns.map((c) => c.campaignType)),
    peerScope: enumeration(['destination', 'country', 'global']),
    minN: enumeration(['3', '5', '10']),
    search: { type: 'text' as const, maxLength: 200 },
    ageMin: enumeration([current.ageMin || '', '1', '7', '14', '28', '42', '60', '90', '180', '365']),
    ageMax: enumeration([current.ageMax || '', '1', '7', '14', '28', '42', '60', '90', '180', '365']),
    launchFrom: { type: 'date' as const },
    launchTo: { type: 'date' as const },
    observationFrom: { type: 'date' as const },
    observationTo: { type: 'date' as const },
  };
}
export function applyFlashFilters(search: string, changes: Record<string, unknown>, campaigns: FlashCampaign[]) {
  const current = Core.fromSearch(search),
    next = { ...current, ...changes } as FlashFilters,
    nonEmpty = Object.fromEntries(Object.entries(changes).filter(([, v]) => v !== ''));
  validatePageFilters(nonEmpty, flashFilterSchema(campaigns, next));
  if (Boolean(next.reviewBy) !== Boolean(next.reviewGroup)) throw Error('Choose both the review grouping and group.');
  if (next.reviewGroup && !flashFilterSchema(campaigns, next).reviewGroup.values.includes(next.reviewGroup))
    throw Error('Unsupported review group.');
  for (const [key, value] of Object.entries(changes)) {
    if (!(key in flashFilterSchema(campaigns, current))) throw Error('Unsupported filter: ' + key);
    if (typeof value !== 'string') throw Error('Filter values must be text.');
  }
  const params = new URLSearchParams(search);
  for (const [key, value] of Object.entries(changes)) {
    if (key === 'flashCampaign') params.delete('campaign');
    if (key === 'flashCohort') params.delete('cohort');
    if (key === 'view') {
      params.delete('view');
      params.set('flashView', String(value));
    } else if (key === 'weeklyExclusion') params.set(key, Core.exclusionIds(String(value)).join(','));
    else params.set(key, String(value));
  }
  return params;
}
export function flashComparators(campaigns: FlashCampaign[], campaignIds: string[], selectedId?: string) {
  const members = new Set(campaignIds);
  return campaigns.filter((c) => members.has(c.id) && c.id !== selectedId);
}

export function flashBridgeFilterSchema(
  campaigns: FlashCampaign[],
  current: Partial<FlashFilters>,
  queue: FlashCampaign[],
) {
  const full = flashFilterSchema(campaigns, current);
  const { flashCampaign: _legacyCampaign, flashCohort: _legacyCohort, ...schema } = full;
  schema.weeklyExclusion.values = [
    ...new Set([
      '',
      ...Core.exclusionIds(current.weeklyExclusion),
      ...(current.view === 'portfolio' && current.historyView === 'weekly'
        ? campaigns
            .filter(
              (c) =>
                !current.search ||
                `${c.opportunityName || c.name} ${c.name}`.toLowerCase().includes(current.search.toLowerCase()),
            )
            .slice(0, 40)
            .map((c) => c.id)
        : []),
    ]),
  ];
  schema.campaign.values = ['', ...queue.map((c) => c.id)];
  return schema;
}
